import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { CheckGlyph, EyeGlyph, EyeOffGlyph } from "./userui";

/* The password picker shared by Add user and Reset password (user.css .bmpw):
   generate one, or type one to hand over yourself. An admin sets it through
   PUT /user's pwd; the gateway ignores a pwd sent with POST /user. */

export const PASSWORD_MIN = 8;

export const PASSWORD_MODES = [
  {
    key: "auto",
    title: "Automatically generate a password",
    note: "You'll be able to view and copy the password in the next step",
  },
  {
    key: "create",
    title: "Create password",
    note: "Type a password to share with them yourself",
  },
];

// Why the typed password can't be used yet, or null.
export const passwordProblem = (mode, password) =>
  mode === "create" && (password || "").length < PASSWORD_MIN
    ? `Password must have at least ${PASSWORD_MIN} characters`
    : null;

// Something a person can read aloud: no 0/O or 1/l/I. One of each kind, so it
// passes the reset page's rules too.
const SETS = ["ABCDEFGHJKLMNPQRSTUVWXYZ", "abcdefghijkmnopqrstuvwxyz", "23456789", "!@#$%&*"];
const randomInt = (n) => {
  const c = typeof window !== "undefined" && window.crypto;
  if (c && c.getRandomValues) {
    const buf = new Uint32Array(1);
    c.getRandomValues(buf);
    return buf[0] % n;
  }
  return Math.floor(Math.random() * n);
};
export const generatePassword = (length = 14) => {
  const all = SETS.join("");
  const chars = SETS.map((s) => s[randomInt(s.length)]);
  while (chars.length < length) chars.push(all[randomInt(all.length)]);
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomInt(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
};

// The PUT /user body that sets a user's password. must_change_password is for
// the gateway to act on; today it ignores the field.
export const passwordUpdate = (user, pwd, askChange) => ({
  rowid: user.id,
  email: user.email,
  pwd,
  must_change_password: Boolean(askChange),
});

export const PasswordChoice = ({ mode, setMode, password, setPassword, askChange, setAskChange, error }) => {
  const [shown, setShown] = useState(false);
  return (
    <div className="bmpw">
      <div className="bmpw-opts" role="radiogroup" aria-label="Password">
        {PASSWORD_MODES.map((m) => (
          <label key={m.key} className={`bmpw-opt${mode === m.key ? " on" : ""}`}>
            <input
              type="radio"
              name="bmpw-mode"
              value={m.key}
              checked={mode === m.key}
              onChange={() => setMode(m.key)}
            />
            <span>
              <b>{m.title}</b>
              <small>{m.note}</small>
            </span>
          </label>
        ))}
      </div>

      {mode === "create" && (
        <div className={`bmpw-new${error ? " bad" : ""}`}>
          <label htmlFor="bmpw-pass">
            Password <span className="req">*</span>
          </label>
          <div className="bmpw-input">
            <input
              id="bmpw-pass"
              type={shown ? "text" : "password"}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShown((s) => !s)}
              aria-label={shown ? "Hide password" : "Show password"}
              title={shown ? "Hide password" : "Show password"}
            >
              {shown ? EyeOffGlyph : EyeGlyph}
            </button>
          </div>
          <p className="bmpw-hint">{error || `Password must have at least ${PASSWORD_MIN} characters`}</p>
        </div>
      )}

      <label className="bmpw-ask">
        <input type="checkbox" checked={askChange} onChange={(e) => setAskChange(e.target.checked)} />
        Ask user to change their password when they sign in
      </label>
    </div>
  );
};

// The generated password, once, with a copy button.
export const PasswordReveal = ({ password }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
    } catch (e) {
      const t = document.createElement("textarea");
      t.value = password;
      document.body.appendChild(t);
      t.select();
      document.execCommand("copy");
      t.remove();
    }
    setCopied(true);
  };
  return (
    <div className="bmpw-reveal">
      <code aria-label="New password">{password}</code>
      <button type="button" onClick={copy}>
        {copied ? CheckGlyph : null}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

// A small dialog in the user sheet's look: icon tile, title, body, footer.
export const PasswordDialog = ({ icon, title, sub, onClose, footer, children }) => (
  <div className="bmuf-layer sm">
    <div className="bmuf bmuf-sm" role="dialog" aria-modal="true" aria-labelledby="bmpd-title">
      <div className="bmuf-head">
        <span className="bmuf-tile" aria-hidden="true">{icon}</span>
        <div className="bmuf-head-text">
          <h2 id="bmpd-title">{title}</h2>
          {sub && <p>{sub}</p>}
        </div>
        <button type="button" className="bmuf-x" onClick={onClose} aria-label="Close" title="Close">
          <CloseIcon style={{ fontSize: 21 }} />
        </button>
      </div>
      <div className="bmuf-body">{children}</div>
      <div className="bmuf-foot">{footer}</div>
    </div>
  </div>
);
